import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { Series } from './linechart.model';
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

am4core.useTheme(am4themes_animated);
am4core.options.commercialLicense = true;

@Component({
    name: 'linechart',
    components: {
    },
})
export default class LineChartComponent extends Vue {
    readonly chartData: Series[] = [];
    private readonly id: string = "chartDiv_column";
    private readonly name: string = "Multi Line Chart";
    private chart: any;

    mounted(): void {
        this.initialization();
        console.log("mounted called");
    }

    updated(): void {
        this.initialization();
        console.log("updated called");
    }

    private initialization(): void {
        var series = [...this.chartData];
        this.reset();
        series.forEach(s => {
            this.pushSeries(s);
        });
    }

    reset(): void {
        this.chartData.splice(0);
        this.disposeChart();
        this.initChart();
    }

    /**
     * Adds a new series to the graph. 
     * * @param series
     */
    pushSeries(series: Series): void {
        if (this.chart === undefined || this.chart._disposed) {
            this.initChart();
        }
        this.chart.data = this.chart.data.concat(this.maptoseries(series));
        var xAxis = this.createXAxis(series.color!);
        var yAxis = this.createYAxis(series.color!, series.unit!);
        var ignore = this.createSeries(series, yAxis, xAxis);

        this.chartData.push(series);
    }

    //util
    /**
    * Initializes an empty chart with a legend at the bottom of the chart
    * */
    private initChart(): void {
        // Create chart
        this.chart = am4core.create(this.id, am4charts.XYChart);

        this.chart.paddingTop = 40; //important for yaxis title on top

        if (this.$i18n.locale.startsWith('de')) {
            this.chart.language.locale["_decimalSeparator"] = ",";
            this.chart.language.locale["_thousandSeparator"] = ".";
        }

        this.chart.cursor = new am4charts.XYCursor();

        this.chart.legend = new am4charts.Legend();
        this.chart.legend.parent = this.chart.plotContainer;
        this.chart.legend.zIndex = 100;
        this.chart.legend.align = "bottom";
        this.chart.legend.valign = "bottom";
        this.chart.legend.useDefaultMarker = true;

        let marker = this.chart.legend.markers.template.children.getIndex(0);
        marker.cornerRadius(8, 8, 8, 8);
        marker.strokeWidth = 2;
        marker.strokeOpacity = 1;
        marker.stroke = am4core.color("#ccc");
    }

    /**
    * Disposes the chart and also removes the local storage item
    * */
    private disposeChart(): void {
        if (this.chart !== undefined)
            this.chart.dispose();
    }


    /**
     * Creates an xAxis for the chart
     * @param color
     */
    private createXAxis(color: string): any {
        let axis = this.chart.xAxes.push(new am4charts.DateAxis());
        axis.tooltipDateFormat = this.$t('dateTimeFormatString').toString();
        axis.renderer.grid.template.location = 0;
        axis.renderer.grid.template.strokeOpacity = 0.07;
        axis.renderer.labels.template.fill = am4core.color(color);
        return axis;
    }

    /**
     * Creates a y axis for the chart
     * @param color
     */
    private createYAxis(color: string, unit: string): any {
        let axis = this.chart.yAxes.push(new am4charts.ValueAxis());
        axis.title.text = unit;
        axis.title.fill = am4core.color(color);
        axis.title.rotation = 0;
        axis.title.align = "center";
        axis.title.valign = "top";
        axis.title.dy = -40;
        axis.title.dx = 40;
        axis.title.fontWeight = 600;

        axis.tooltip.disabled = true;

        axis.renderer.grid.template.strokeDasharray = "2,3";
        axis.renderer.labels.template.fill = am4core.color(color);
        axis.renderer.minWidth = 60;
        axis.renderer.grid.template.strokeOpacity = 0.07;

        return axis;
    }

    /**
     * Creates a series that is added to the chart
     * @param data
     * @param valueAxis
     * @param dateAxis
     */
    private createSeries(s: Series, valueAxis, dateAxis): any {
        let series = this.chart.series.push(new am4charts.LineSeries());
        series.name = s.label;
        series.dataFields.dateX = s.xpropname;
        series.dataFields.valueY = s.ypropname;
        series.yAxis = valueAxis;
        series.xAxis = dateAxis;
        series.tooltipText = "{valueY.value} " + s.unit;
        series.fill = am4core.color(s.color);
        series.stroke = am4core.color(s.color);
        return series;
    }

    private maptoseries(series: Series): any[] {
        if (series.data === undefined)
            return [];
        var kx = series.xpropname ?? '';
        var ky = series.ypropname ?? '';
        return series.data.map(x => {
            var o = {};
            o[kx] = x.timeStamp;
            o[ky] = x.value;
            return o;
        });
    }
}
